import React from 'react'
import clsx from 'clsx'
import { useDrag, useDrop } from 'react-dnd'
import { MdDragHandle } from 'react-icons/md'
import {
  AddCircle,
  ArrowDown2,
  CloseCircle,
  Edit2,
  TickCircle
} from 'iconsax-react'
import { IconButton, useTheme } from '@mui/material'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'

import OverflowText from '@SonarCX/components/atoms/OverflowText'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import CustomFieldOptionCondition from '../CustomFieldOptionCondition'
import useBaseStyles from '../CustomField/styles'
import useStyles from './styles'

const ITEM_TYPE = 'OPTION'
type DragItem = {
  type: typeof ITEM_TYPE
  id: string
}

type CustomFieldOptionProps = {
  id: string
  handleMoveOption: (start: string, end: string) => void
  handleRemoveOption: (id: string) => void
  handleAddCondition?: (
    id: string,
    condition: APICustomFieldConfigOptionCondition
  ) => void
  handleRemoveCondition?: (val: string, index: number) => void
  handleUpdateCondition?(
    val: string,
    index: number,
    condition: APICustomFieldConfigOptionCondition
  )
  isLast?: boolean
  renderItem?: (id: string) => string
  type?: APICustomField['object_type']
  option?: APICustomFieldConfigOption
  editing?: string
  showConditions?: boolean
  handleRenameOption?: (id: string, newId: string) => void
  enableNameEdit?: boolean
}

const CustomFieldOption: React.FC<CustomFieldOptionProps> = ({
  id,
  isLast,
  option = { val: '', conditions: [] },
  type,
  editing,
  showConditions,
  enableNameEdit = false,
  handleMoveOption,
  handleRemoveOption,
  handleAddCondition,
  handleUpdateCondition,
  handleRemoveCondition,
  handleRenameOption,
  renderItem
}) => {
  const [expanded, setExpanded] = React.useState(false)
  const [isEditingName, setIsEditingName] = React.useState(false)
  const [nameToEdit, setNameToEdit] = React.useState(id)
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const [{ opacity }, dragRef, previewRef] = useDrag({
    item: { type: ITEM_TYPE, id },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  })

  const [{ isOver: isOverTop }, dropTop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: DragItem) => handleMoveOption(item.id, id),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottom] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: DragItem) => handleMoveOption(item.id, id),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const name = React.useMemo(() => {
    if (!renderItem) {
      return id
    }

    return renderItem(id)
  }, [renderItem, id])

  const handleClickAddCondition = React.useCallback(() => {
    handleAddCondition(id, {
      field_id: '',
      negate: false,
      match: ['']
    } as APICustomFieldConfigOptionCondition)
  }, [id, handleAddCondition])

  const confirmEdit = React.useCallback(() => {
    if (nameToEdit !== id) {
      handleRenameOption(id, nameToEdit)
    }
    setIsEditingName(false)
    setNameToEdit(id)
  }, [handleRenameOption, id, nameToEdit])

  const handleCancelEdit = React.useCallback(() => {
    setIsEditingName(false)
    setNameToEdit(id)
  }, [id])

  const handleOnKeyDown = React.useCallback(
    e => {
      const { key } = e

      if (key === 'Enter') {
        confirmEdit()
        return
      }

      if (key !== 'Escape') {
        return
      }

      e.stopPropagation()
      if (nameToEdit !== id) {
        setReason('Unsaved changes will be lost.')
        setSubtext('Are you sure?')
        setTitle('ARE YOU SURE?')
        setV2(true)
        setAction(handleCancelEdit)
      } else {
        handleCancelEdit()
      }
    },
    [
      id,
      nameToEdit,
      confirmEdit,
      handleCancelEdit,
      setAction,
      setReason,
      setSubtext,
      setTitle,
      setV2
    ]
  )

  const theme = useTheme()
  const baseClasses = useBaseStyles()
  const classes = useStyles({
    isOverTop,
    isOverBottom,
    expanded
  })

  return (
    <>
      <div ref={dropTop} className={clsx(classes.drop, classes.dropTop)} />

      <div
        ref={previewRef}
        className={clsx(classes.root, classes.chip)}
        style={{ opacity }}
      >
        <div className={classes.descRow}>
          <div
            ref={dragRef}
            className={baseClasses.drag}
            style={{ cursor: 'grab' }}
          >
            <MdDragHandle />
          </div>
          {isEditingName ? (
            <input
              autoFocus
              className={classes.label}
              value={nameToEdit}
              onChange={e => {
                setNameToEdit(e.target.value)
              }}
              onKeyDown={handleOnKeyDown}
            />
          ) : (
            <OverflowText className={classes.label}>{name}</OverflowText>
          )}
          {isEditingName && (
            <span>
              <Tooltip title='cancel edit' arrow>
                <IconButton size='small' onClick={handleCancelEdit}>
                  <CloseCircle size={theme.size.sm} />
                </IconButton>
              </Tooltip>
              <Tooltip title='confirm' arrow>
                <IconButton size='small' onClick={confirmEdit}>
                  <TickCircle size={theme.size.sm} />
                </IconButton>
              </Tooltip>
            </span>
          )}
          {!isEditingName && (
            <span className={classes.chipOptions}>
              {enableNameEdit && (
                <Tooltip title='edit' arrow>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setIsEditingName(true)
                    }}
                  >
                    <Edit2 size={theme.size.sm} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title='remove' arrow>
                <IconButton size='small' onClick={() => handleRemoveOption(id)}>
                  <CloseCircle size={theme.size.sm} />
                </IconButton>
              </Tooltip>

              {showConditions && (
                <Tooltip
                  title={expanded ? 'hide conditions' : 'show conditions'}
                  arrow
                >
                  <IconButton
                    size='small'
                    onClick={() => setExpanded(old => !old)}
                  >
                    <ArrowDown2
                      className={classes.expandIcon}
                      size={theme.size.sm}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </span>
          )}
        </div>

        {expanded && (
          <div className={classes.expandedRoot}>
            <span className={classes.conditionsLabel}>Show when</span>
            {option.conditions.length === 0 ? (
              <small>always</small>
            ) : (
              option.conditions.map((condition, i) => (
                <CustomFieldOptionCondition
                  key={`${id}-${i}`}
                  condition={condition}
                  index={i}
                  id={id}
                  onRemoveCondition={handleRemoveCondition}
                  onUpdateCondition={handleUpdateCondition}
                  type={type}
                  editing={editing}
                />
              ))
            )}
            <IconButton
              size='small'
              className={classes.addConditionBtn}
              onClick={handleClickAddCondition}
            >
              <AddCircle
                size={theme.size.sm}
                color={theme.palette.primary.main}
              />
            </IconButton>
          </div>
        )}
      </div>

      {isLast && (
        <div
          ref={dropBottom}
          className={clsx(classes.drop, classes.dropBottom)}
        />
      )}
    </>
  )
}

export default CustomFieldOption
